import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { Edit2, Info, Link2, Lock, RotateCcw, Search, Trash2, UserPlus } from 'react-feather';
import { toast } from 'react-toastify';
import {
  AVISO_MCP,
  AVISO_PROPAGACION,
  AYUDA_VINCULAR,
  DEBOUNCE_MS,
  ESTADO_TODOS,
  ESTADOS_FILTRO,
  LIMITE_LISTADO,
  MCP_HEADQUARTER,
  MSG_SIN_PERMISO,
  NOTAS_MAX,
  PAGE_SIZE,
  calcularCambios,
  mensajeError,
  normalizarEmail,
  normalizarNotas,
  useCrearMcpUsuario,
  useEditarMcpUsuario,
  useEliminarMcpUsuario,
  useMcpUsuariosLista,
  validarFormulario,
} from '../../features/mcpUsuarios';
import { useDebouncedValue } from '../../features/precios';
import Loader from '../../layouts/loader/Loader';
import colors from '../../theme/colors';

// Gestion de mcp_usuarios_autorizados (quien puede entrar al MCP de alumnos).
// La vista /ajustes ya es solo SUPERADMIN; el backend exige ademas ser gestor del
// MCP (MCP_GESTORES) y responde 403 al resto, que aqui se muestra sin tabla.

const FORM_ID = 'form-usuario-mcp';
const SIN_VALOR = '—';

const thStyle = {
  textAlign: 'left',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  padding: '10px 14px',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '11px 14px',
  verticalAlign: 'middle',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: colors.textSecondary,
  marginBottom: '6px',
};

const avisoVacioStyle = {
  padding: '32px',
  textAlign: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: '8px',
  background: colors.bgCard,
  fontSize: '0.875rem',
};

const TONOS = {
  ok: { background: `${colors.success}18`, color: colors.successDark },
  neutro: { background: 'rgba(107, 114, 128, 0.1)', color: '#4b5563' },
  aviso: { background: 'rgba(217, 119, 6, 0.1)', color: '#b45309' },
};

const Etiqueta = ({ tono, title = undefined, children }) => (
  <span
    title={title}
    style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      ...TONOS[tono],
    }}
  >
    {children}
  </span>
);

Etiqueta.propTypes = {
  tono: PropTypes.oneOf(['ok', 'neutro', 'aviso']).isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const TONOS_BOTON = {
  neutro: { border: colors.borderStrong, color: colors.text },
  peligro: { border: colors.danger, color: colors.dangerDark },
  primario: { border: colors.primaryDark, color: colors.primaryDark },
};

const BotonFila = ({ tono, icon: Icon, label, ariaLabel, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '5px 10px',
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: '6px',
      border: `1px solid ${TONOS_BOTON[tono].border}`,
      background: colors.bgCard,
      color: TONOS_BOTON[tono].color,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    <Icon size={13} aria-hidden="true" /> {label}
  </button>
);

BotonFila.propTypes = {
  tono: PropTypes.oneOf(['neutro', 'peligro', 'primario']).isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const CeldaAuditoria = ({ fecha = null, por = null }) => {
  if (!fecha && !por) return SIN_VALOR;
  return (
    <div>
      <div style={{ fontVariantNumeric: 'tabular-nums', color: colors.text }}>
        {fecha || SIN_VALOR}
      </div>
      {por && <div style={{ fontSize: '0.75rem', color: colors.textSecondary }}>{por}</div>}
    </div>
  );
};

CeldaAuditoria.propTypes = {
  fecha: PropTypes.string,
  por: PropTypes.string,
};

const usuarioShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  activo: PropTypes.bool,
  notas: PropTypes.string,
  cuentaVinculada: PropTypes.bool,
  fechaAlta: PropTypes.string,
  creadoPor: PropTypes.string,
  fechaModificacion: PropTypes.string,
  modificadoPor: PropTypes.string,
});

// Alta (usuario null) o edicion. El padre lo monta con key por fila para que el
// estado inicial salga de la fila sin efectos de sincronizacion.
const ModalUsuarioMcp = ({
  isOpen,
  usuario = null,
  guardando = false,
  onGuardar,
  onCerrar,
  onClosed = undefined,
}) => {
  const esEdicion = Boolean(usuario);
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [activo, setActivo] = useState(usuario?.activo ?? true);
  const [notas, setNotas] = useState(usuario?.notas ?? '');
  const [intentado, setIntentado] = useState(false);

  const errores = useMemo(() => validarFormulario({ email, notas }), [email, notas]);
  const cambios = useMemo(
    () => calcularCambios(usuario, { email, activo, notas }),
    [usuario, email, activo, notas],
  );
  const hayErrores = Object.keys(errores).length > 0;
  const hayCambios = Object.keys(cambios).length > 0;
  const mostrarErrores = intentado || esEdicion;
  const emailCambiado = esEdicion && 'email' in cambios;

  const cerrar = () => {
    if (!guardando) onCerrar();
  };

  const enviar = (e) => {
    e.preventDefault();
    setIntentado(true);
    if (guardando || hayErrores) return;
    if (esEdicion) {
      if (hayCambios) onGuardar(cambios);
      return;
    }
    const notasNorm = normalizarNotas(notas);
    onGuardar({ email: normalizarEmail(email), ...(notasNorm ? { notas: notasNorm } : {}) });
  };

  const titulo = esEdicion ? 'Editar usuario del MCP' : 'Añadir usuario al MCP';

  return (
    <Modal isOpen={isOpen} toggle={cerrar} onClosed={onClosed} centered>
      <ModalHeader toggle={cerrar}>{titulo}</ModalHeader>
      <ModalBody>
        <Form id={FORM_ID} onSubmit={enviar} noValidate>
          <FormGroup>
            <Label for="mcp-email" style={labelStyle}>
              Email
            </Label>
            <Input
              id="mcp-email"
              type="email"
              autoComplete="off"
              placeholder="persona@cedetecgroup.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={mostrarErrores && Boolean(errores.email)}
              disabled={guardando}
            />
            {mostrarErrores && errores.email && <FormFeedback>{errores.email}</FormFeedback>}
            {emailCambiado && !errores.email && (
              <FormText style={{ color: colors.textSecondary }}>
                Se volverá a comprobar la cuenta en Google.
              </FormText>
            )}
          </FormGroup>
          {esEdicion && (
            <FormGroup switch>
              <Input
                id="mcp-activo"
                type="switch"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                disabled={guardando}
              />
              <Label for="mcp-activo" check>
                Activo
              </Label>
            </FormGroup>
          )}
          <FormGroup className="mb-0">
            <Label for="mcp-notas" style={labelStyle}>
              Notas (opcional)
            </Label>
            <Input
              id="mcp-notas"
              type="textarea"
              rows={2}
              value={notas}
              maxLength={NOTAS_MAX}
              onChange={(e) => setNotas(e.target.value)}
              invalid={mostrarErrores && Boolean(errores.notas)}
              disabled={guardando}
              aria-describedby="mcp-notas-ayuda"
            />
            {mostrarErrores && errores.notas && <FormFeedback>{errores.notas}</FormFeedback>}
            <div
              id="mcp-notas-ayuda"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                marginTop: '4px',
                fontSize: '0.75rem',
                color: colors.textSecondary,
              }}
            >
              <span>Sin datos de alumnos ni datos sensibles.</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {notas.length}/{NOTAS_MAX}
              </span>
            </div>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={cerrar} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          color="primary"
          type="submit"
          form={FORM_ID}
          disabled={guardando || (esEdicion && (hayErrores || !hayCambios))}
        >
          {guardando ? <Spinner size="sm" /> : null} {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalUsuarioMcp.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  usuario: usuarioShape,
  guardando: PropTypes.bool,
  onGuardar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
  onClosed: PropTypes.func,
};

const ModalEliminarUsuarioMcp = ({ usuario = null, eliminando = false, onConfirmar, onCerrar }) => {
  const cerrar = () => {
    if (!eliminando) onCerrar();
  };
  return (
    <Modal isOpen={usuario !== null} toggle={cerrar} centered>
      <ModalHeader toggle={cerrar}>Eliminar usuario del MCP</ModalHeader>
      <ModalBody>
        <p style={{ marginBottom: 0, color: colors.text }}>
          Se borrará definitivamente a <strong>{usuario?.email}</strong> de la lista del MCP. Para
          retirar el acceso de forma temporal, desactívalo en su lugar.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={cerrar} disabled={eliminando}>
          Cancelar
        </Button>
        <Button color="danger" onClick={onConfirmar} disabled={eliminando}>
          {eliminando ? <Spinner size="sm" /> : <Trash2 size={15} aria-hidden="true" />}{' '}
          {eliminando ? 'Eliminando…' : 'Eliminar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalEliminarUsuarioMcp.propTypes = {
  usuario: usuarioShape,
  eliminando: PropTypes.bool,
  onConfirmar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

const GestionUsuariosMcp = () => {
  const headquarter = MCP_HEADQUARTER;

  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState(ESTADO_TODOS);
  const search = useDebouncedValue(busqueda.trim(), DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ abierto: false, usuario: null, clave: 0 });
  const [aEliminar, setAEliminar] = useState(null);
  const [vinculandoId, setVinculandoId] = useState(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useMcpUsuariosLista({
    headquarter,
    search,
    estado,
  });
  const crear = useCrearMcpUsuario(headquarter);
  const editar = useEditarMcpUsuario(headquarter);
  const eliminar = useEliminarMcpUsuario(headquarter);

  useEffect(() => {
    setPage(1);
  }, [search, estado]);

  const usuarios = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil(usuarios.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const inicio = (pageSafe - 1) * PAGE_SIZE;
  const visibles = usuarios.slice(inicio, inicio + PAGE_SIZE);
  const hayFiltros = Boolean(search) || estado !== ESTADO_TODOS;
  const haySinVincular = usuarios.some((u) => !u.cuentaVinculada);

  if (isError && error?.response?.status === 403) {
    return (
      <div role="alert" style={{ ...avisoVacioStyle, color: colors.textSecondary }}>
        <Lock size={16} aria-hidden="true" style={{ marginRight: '8px', verticalAlign: '-3px' }} />
        {MSG_SIN_PERMISO}
      </div>
    );
  }

  const abrirAlta = () => setModal((m) => ({ abierto: true, usuario: null, clave: m.clave + 1 }));
  const abrirEdicion = (usuario) =>
    setModal((m) => ({ abierto: true, usuario, clave: m.clave + 1 }));
  const cerrarModal = () => setModal((m) => ({ ...m, abierto: false }));

  const errorConRecarga = (err, generico) => {
    toast.error(mensajeError(err, generico));
    if (err?.response?.status === 404) refetch();
  };

  const guardar = (payload) => {
    const { usuario } = modal;
    if (!usuario) {
      crear.mutate(payload, {
        onSuccess: () => {
          toast.success(`${payload.email} añadido al MCP. ${AVISO_PROPAGACION}`);
          cerrarModal();
        },
        onError: (err) => toast.error(mensajeError(err, 'No se pudo añadir el usuario.')),
      });
      return;
    }
    editar.mutate(
      { id: usuario.id, cambios: payload },
      {
        onSuccess: () => {
          toast.success(`${payload.email ?? usuario.email} actualizado. ${AVISO_PROPAGACION}`);
          cerrarModal();
        },
        onError: (err) => {
          errorConRecarga(err, 'No se pudo guardar el usuario.');
          if (err?.response?.status === 404) cerrarModal();
        },
      },
    );
  };

  const vincular = (usuario) => {
    setVinculandoId(usuario.id);
    editar.mutate(
      { id: usuario.id, cambios: { email: usuario.email } },
      {
        onSuccess: () =>
          toast.success(`Cuenta de ${usuario.email} vinculada. ${AVISO_PROPAGACION}`),
        onError: (err) => errorConRecarga(err, 'No se pudo vincular la cuenta.'),
        onSettled: () => setVinculandoId(null),
      },
    );
  };

  const confirmarEliminar = () => {
    const objetivo = aEliminar;
    eliminar.mutate(
      { id: objetivo.id },
      {
        onSuccess: () => {
          toast.success(`${objetivo.email} eliminado del MCP. ${AVISO_PROPAGACION}`);
          setAEliminar(null);
        },
        onError: (err) => {
          errorConRecarga(err, 'No se pudo eliminar el usuario.');
          setAEliminar(null);
        },
      },
    );
  };

  const renderListado = () => {
    if (isLoading) return <Loader />;
    if (isError) {
      return (
        <div role="alert" style={{ ...avisoVacioStyle, color: colors.dangerDark }}>
          No se pudo cargar la lista de usuarios del MCP.{' '}
          <Button color="link" size="sm" onClick={() => refetch()} style={{ padding: 0 }}>
            Reintentar
          </Button>
        </div>
      );
    }
    if (usuarios.length === 0) {
      return (
        <div style={{ ...avisoVacioStyle, color: colors.textSecondary }}>
          {hayFiltros
            ? 'No hay usuarios que coincidan con los filtros.'
            : 'Todavía no hay usuarios en la lista del MCP.'}
        </div>
      );
    }
    return (
      <div style={{ opacity: isFetching ? 0.6 : 1 }} aria-busy={isFetching}>
        {data?.truncado && (
          <div role="status" style={{ marginBottom: '8px', fontSize: '0.8rem', color: '#b45309' }}>
            Se muestran los {LIMITE_LISTADO} primeros: afina el filtro.
          </div>
        )}
        {haySinVincular && (
          <div
            role="note"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '10px 14px',
              marginBottom: '12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bg,
              color: colors.textSecondary,
              fontSize: '0.8rem',
            }}
          >
            <Link2
              size={15}
              aria-hidden="true"
              style={{ color: colors.primaryDark, flexShrink: 0, marginTop: '2px' }}
            />
            <span>
              <strong style={{ color: colors.text }}>¿Qué hace «Vincular»?</strong> {AYUDA_VINCULAR}
            </span>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              overflow: 'hidden',
              fontSize: '0.875rem',
            }}
          >
            <thead>
              <tr style={{ background: colors.bg }}>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Cuenta</th>
                <th style={thStyle}>Notas</th>
                <th style={thStyle}>Alta</th>
                <th style={thStyle}>Último cambio</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((u) => (
                <tr key={u.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                  <td style={{ ...tdStyle, fontWeight: 500, wordBreak: 'break-all' }}>{u.email}</td>
                  <td style={tdStyle}>
                    {u.activo ? (
                      <Etiqueta tono="ok">Activo</Etiqueta>
                    ) : (
                      <Etiqueta tono="neutro">Inactivo</Etiqueta>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {u.cuentaVinculada ? (
                      <Etiqueta tono="neutro" title="Vinculada a la cuenta de Google Workspace">
                        Vinculada
                      </Etiqueta>
                    ) : (
                      <Etiqueta
                        tono="aviso"
                        title="Fila antigua sin la cuenta de Google vinculada: no da acceso al MCP. Pulsa Vincular para asociarla."
                      >
                        Sin vincular
                      </Etiqueta>
                    )}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      color: colors.textSecondary,
                      minWidth: '160px',
                      maxWidth: '280px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {u.notas || SIN_VALOR}
                  </td>
                  <td style={tdStyle}>
                    <CeldaAuditoria fecha={u.fechaAlta} por={u.creadoPor} />
                  </td>
                  <td style={tdStyle}>
                    <CeldaAuditoria fecha={u.fechaModificacion} por={u.modificadoPor} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '6px',
                      }}
                    >
                      {!u.cuentaVinculada && (
                        <BotonFila
                          tono="primario"
                          icon={Link2}
                          label={vinculandoId === u.id ? 'Vinculando…' : 'Vincular'}
                          ariaLabel={`Vincular la cuenta de ${u.email}`}
                          onClick={() => vincular(u)}
                          disabled={vinculandoId !== null}
                        />
                      )}
                      <BotonFila
                        tono="neutro"
                        icon={Edit2}
                        label="Editar"
                        ariaLabel={`Editar ${u.email}`}
                        onClick={() => abrirEdicion(u)}
                      />
                      <BotonFila
                        tono="peligro"
                        icon={Trash2}
                        label="Eliminar"
                        ariaLabel={`Eliminar ${u.email}`}
                        onClick={() => setAEliminar(u)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '12px',
            fontSize: '0.8rem',
            color: colors.textSecondary,
          }}
        >
          <span aria-live="polite">
            {inicio + 1}–{Math.min(inicio + PAGE_SIZE, usuarios.length)} de {usuarios.length}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              color="light"
              size="sm"
              onClick={() => setPage(Math.max(1, pageSafe - 1))}
              disabled={pageSafe <= 1}
            >
              Anterior
            </Button>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {pageSafe} / {totalPages}
            </span>
            <Button
              color="light"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, pageSafe + 1))}
              disabled={pageSafe >= totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <p
        style={{
          color: colors.textSecondary,
          marginTop: 0,
          marginBottom: '16px',
          fontSize: '0.8rem',
        }}
      >
        Personal que puede consultar datos de alumnos a través del MCP. La cuenta se comprueba en
        Google Workspace al guardar.
      </p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.textSecondary,
          fontSize: '0.8rem',
        }}
      >
        <Info size={15} aria-hidden="true" style={{ color: colors.infoDark, flexShrink: 0 }} />
        <span>{AVISO_MCP}</span>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: 2, minWidth: '220px' }}>
          <label htmlFor="mcp-filtro" style={labelStyle}>
            Buscar
          </label>
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textSecondary,
              }}
            />
            <input
              id="mcp-filtro"
              type="search"
              className="form-control"
              placeholder="Email o notas"
              value={busqueda}
              maxLength={100}
              autoComplete="off"
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ paddingLeft: '32px' }}
            />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label htmlFor="mcp-estado" style={labelStyle}>
            Estado
          </label>
          <select
            id="mcp-estado"
            className="form-select"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            {ESTADOS_FILTRO.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          color="light"
          onClick={() => refetch()}
          title="Recargar listado"
          aria-label="Recargar listado"
          disabled={isFetching}
        >
          <RotateCcw size={15} aria-hidden="true" />
        </Button>
        <Button color="primary" onClick={abrirAlta} style={{ marginLeft: 'auto' }}>
          <UserPlus size={15} aria-hidden="true" /> Añadir
        </Button>
      </div>

      {renderListado()}

      <ModalUsuarioMcp
        key={modal.clave}
        isOpen={modal.abierto}
        usuario={modal.usuario}
        guardando={modal.usuario ? editar.isPending : crear.isPending}
        onGuardar={guardar}
        onCerrar={cerrarModal}
      />

      <ModalEliminarUsuarioMcp
        usuario={aEliminar}
        eliminando={eliminar.isPending}
        onConfirmar={confirmarEliminar}
        onCerrar={() => setAEliminar(null)}
      />
    </div>
  );
};

export default GestionUsuariosMcp;
